import React from 'react';
import useSessionStore from '../state/session';
import { requestSessionRetry } from './AuthSessionCoordinator';

type ClientPortalRouteGateProps = {
  children: React.ReactNode;
};

/**
 * Client portal URLs intentionally keep a small unauthenticated recovery
 * surface so a customer with an incomplete or expired invite can be told to
 * reopen the exact link they received. Portal data itself is never public.
 *
 * Do not mount the route component until the coordinator has either proved the
 * principal and loaded its complete access context, or authoritatively proved
 * that there is no session. This keeps stale session/profile state and cached
 * portal queries out of the DOM during getSession/getUser/context validation.
 */
export default function ClientPortalRouteGate({ children }: ClientPortalRouteGateProps) {
  const session = useSessionStore((state) => state.session);
  const authStatus = useSessionStore((state) => state.authStatus);
  const contextStatus = useSessionStore((state) => state.contextStatus);
  const authIssue = useSessionStore((state) => state.authIssue);

  const authenticatedReady = (
    authStatus === 'authenticated'
    && contextStatus === 'ready'
    && Boolean(session?.user?.id)
  );
  const unauthenticatedReady = authStatus === 'unauthenticated' && !session;

  if (authenticatedReady || unauthenticatedReady) return <>{children}</>;

  if (authStatus === 'unavailable' || contextStatus === 'error') {
    const accessDenied = authStatus === 'authenticated' && authIssue?.kind === 'access_denied';
    return (
      <section
        className="section-shell stack gap-md"
        style={{ maxWidth: 560, margin: '2rem auto' }}
        aria-live="polite"
      >
        <h2 className="m0">
          {accessDenied ? 'Access denied' : 'We could not verify your session'}
        </h2>
        <p className="text-sm fg-muted m0">
          {accessDenied
            ? 'You do not have permission to open this information.'
            : 'The service is temporarily unavailable. Your session was kept.'}
        </p>
        {accessDenied ? null : (
          <div>
            <button type="button" className="btn btn-primary" onClick={requestSessionRetry}>
              Try again
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      className="section-shell stack gap-md"
      style={{ maxWidth: 560, margin: '2rem auto' }}
      aria-live="polite"
    >
      <p className="text-sm fg-muted m0">Preparing your secure portal access...</p>
    </section>
  );
}
